import styled from '@emotion/styled';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { connectLocation } from './LocationConnectors';
import bestLocaleMatch from './bestLocaleMatch';
import { makeT, isLocaleMatch } from './locales';
import useDeviceBreakpoint from '../../hooks/use_device_breakpoint';
import Theme from '../../themes/index';
import { SelectDropDown } from '../Builder/Library/Choices';
import Button from '../ComponentLibrary/oc-button';
import Image from '../OC/oc-image';

const BannerContainer = styled.div`
  background: ${'#F8F8F8'};
  display: flex;
  justify-content: center;
  align-items: center;
  padding-top: 8px;
  padding-bottom: 8px;
  .error {
    color: #d65900;
    font-weight: 700;
    margin-right: 16px;
    padding: 0;
  }

  span {
    margin-right: 16px;
  }

  a {
    margin-right: 16px;
  }

  .banner-error {
    margin-left: 16px;
  }

  .country-picker-container {
    width: 300px;
    margin: 12px 16px 12px 0;
  }

  .banner-text {
    display: flex;
    flex-direction: row;
    margin-right: 12px;
  }

  @media screen and (max-width: 768px) {
    flex-direction: column;
    align-items: baseline;
    padding: 12px;
    .country-picker-container {
      width: 94%;
    }
  }
`;

const Option = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: left;

  img {
    width: 38px;
    margin-right: 15px;
  }
`;

const Close = styled.span`
  font-size: 36px;
  &:hover {
    color: ${Theme.colors.brand};
    cursor: pointer;
  }
`;

const OTHER = 'OTHER_COUNTRY';

const navigateToLocations = () => {
  window.location.href = '/locations-languages';
};

// eslint-disable-next-line react/prop-types
const LocalePicker = ({ value, options, onChange }) => (
  <SelectDropDown value={value} options={options} onChange={onChange} validated compact />
);

function makeOption({ code, countryName, languageName, flag }) {
  return {
    value: code,
    label: (
      <Option>
        {flag && <Image src={flag} alt={code} style={{ width: '20px', marginLeft: '8px', marginRight: '8px' }} />}
        <span>{`${_.capitalize(languageName)} (${countryName})`}</span>
      </Option>
    ),
  };
}

function localeSort(a, b) {
  // grouped by country
  const [aLang, aCountry = ''] = a.code.split('-');
  const [bLang, bCountry = ''] = b.code.split('-');
  if (aCountry === bCountry) {
    return aLang.localeCompare(bLang);
  }
  return aCountry.localeCompare(bCountry);
}

const CrossingLocaleBanner = props => {
  const { availableLocales, locale, userLocale, changeUserLocation } = props;
  const mismatched = !isLocaleMatch(locale, userLocale);
  const [hasUserClosed, setHasUserClosed] = useState(
    sessionStorage.getItem('hasUserClosedCrossingLocaleBanner') === 'true'
  );
  const handleClose = () => {
    sessionStorage.setItem('hasUserClosedCrossingLocaleBanner', 'true');
    setHasUserClosed(true);
  };
  const isVisible = mismatched && !hasUserClosed;

  const [selectedLocale, setSelectedLocale] = useState(bestLocaleMatch(userLocale, availableLocales));
  const selected = selectedLocale.other ? OTHER : selectedLocale.code;
  const onLocaleChange = ({ value: nextCode }) => {
    const nextLocale = availableLocales.find(l => l.code === nextCode);
    if (nextLocale) {
      setSelectedLocale(nextLocale);
    } else {
      // hack to make "other" country option
      setSelectedLocale({ ...userLocale, other: true });
    }
  };

  const selectedLocaleCode = selectedLocale.code;
  const selectedT = makeT(selectedLocaleCode);
  const userT = makeT(userLocale.code);

  const handleContinue = () => {
    if (!selectedLocaleCode) {
      throw new Error('Selected locale not found');
    }
    if (selectedLocale.other) {
      navigateToLocations();
    } else {
      changeUserLocation({ locale: selectedLocale });
    }
  };

  const localeOptions = {};
  [...availableLocales].sort(localeSort).forEach(l => {
    localeOptions[l.code] = makeOption(l);
  });
  localeOptions[OTHER] = {
    value: OTHER,
    label: userT('crossing_locale.other_country'),
  };

  const { isPhone } = useDeviceBreakpoint();
  let closeLast = <Close onClick={handleClose}>&times;</Close>;
  let afterText = null;
  if (isPhone) {
    afterText = closeLast;
    closeLast = null;
  }

  return (
    isVisible && (
      <BannerContainer>
        <div className="banner-text">
          {selectedT(`crossing_locale.for_local_information`)}
          {afterText}
        </div>
        <div className="country-picker-container">
          <LocalePicker
            options={Object.values(localeOptions)}
            value={localeOptions[selected]}
            onChange={onLocaleChange}
            data-track="change-location"
            compact
          />
        </div>

        <Button color="white" onClick={handleContinue} data-track="change-zip">
          {selectedT(`crossing_locale.continue`)}
        </Button>
        {closeLast}
      </BannerContainer>
    )
  );
};

export const localeType = PropTypes.shape({
  code: PropTypes.string.isRequired,
  countryName: PropTypes.string,
  flag: PropTypes.string,
  languageName: PropTypes.string,
});

CrossingLocaleBanner.propTypes = {
  availableLocales: PropTypes.arrayOf(localeType).isRequired,
  locale: localeType,
  userLocale: localeType,
  changeUserLocation: PropTypes.func.isRequired,
};

CrossingLocaleBanner.defaultProps = {
  locale: null,
  userLocale: null,
};

export default connectLocation(CrossingLocaleBanner);
